import Icon from '@tiger/icons';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { FormControl } from 'react-bootstrap';
import { EasyField } from 'react-bootstrap-formutil';
import FormGroup, { FormGroupProps } from 'components/FormGroup';
import { type CustomerInfoKeyType, IdTypeEnum } from 'types/customer';
import { CustomerErrorCodeEnum } from 'types/error';
import { printf } from 'utils/i18n';
import { useCheckIdNo, useCheckSSN } from './hooks';
import styles from './styles.scss';

export interface IdNumberFormutil {
    id_no: string;
    mask_id_no: string | null;
}

export interface IdNumberProps extends FormGroupProps {
    source?: 'esop';
    disabled?: boolean;
    autoComplete?: string;
    // 用于id_no掩码填充时的表单校验, 不需要掩码校验时, 可以传null
    maskIdNo: string | null;
    idType: IdTypeEnum;
    label?: string;
    needHide?: boolean;
    // 通过接口校验的配置项，包括可选字段、是否忽略接口校验等
    checkByApiOptions?: {
        // 忽略进行接口的校验
        ignore?: boolean;
        // 用于checkInfo接口，当process为modify时必传
        customerId?: number;
    };
}

export function getFieldsOfIdNumber(maskIdNo: IdNumberProps['maskIdNo']) {
    const fields: CustomerInfoKeyType[] = ['id_no'];

    if (maskIdNo) {
        fields.push('mask_id_no');
    }

    return fields;
}

const IdNumber: React.FC<IdNumberProps> = ({
    source,
    disabled,
    autoComplete,
    maskIdNo,
    checker: customChecker,
    idType,
    label,
    checkByApiOptions,
    needHide,
    name,
    ...restProps
}) => {
    const isSSN = idType === IdTypeEnum.SSN_IDS;
    const defaultLabel = isSSN ? __('SSN') : __('证件号码');
    const finalLabel = label || defaultLabel;
    const { customerId, ignore } = checkByApiOptions || {};
    const { trigger: triggerCheckIdNo } = useCheckIdNo();
    const { trigger: triggerCheckSSN } = useCheckSSN();
    const [passwordShow, setPasswordShow] = useState(false);

    return (
        <>
            {/* mask_id_no的作用: 只是用于校验比较, 不会作为用户身份证明, 可以传给server
            1. 校验id_no, 当id_no与mask_id_no相同时, id_no不需要做校验
            2. 提交的时候, 当id_no与mask_id_no相同时, 不能传id_no */}
            {maskIdNo !== null && <EasyField name="mask_id_no" type="hidden" $defaultValue={maskIdNo} />}
            <FormGroup
                label={finalLabel}
                name={name || 'id_no'}
                helper={
                    isSSN && (
                        <span className="form-text text-muted">
                            {__(
                                'We collect this information to verify your identity as required by the law. Your privacy is important to us.'
                            )}{' '}
                            <a href="https://www.tradeup.com/agreement/206" target="_blank" data-inapp-blank>
                                {__('Learn more')}
                            </a>
                        </span>
                    )
                }
                checker={(value, ...restCheckerParams) => {
                    if (disabled) {
                        return true;
                    }

                    if (!value) {
                        return printf(__('Please enter your %s'), finalLabel);
                    }

                    if (customChecker) {
                        const result = customChecker(value, ...restCheckerParams);

                        // 不能result返回了true就提前结束判断, 因为还有后续判断
                        if (result !== true) {
                            return result;
                        }
                    }

                    // 证件号当前值与掩码相同时, 不做校验
                    if (maskIdNo && maskIdNo === value) {
                        return true;
                    }

                    if (idType === IdTypeEnum.DOMESTIC_IDS) {
                        if (!/^[0-9x]{18}$/i.test(value)) {
                            return __('身份证号码输入不正确(要求18位身份证号码)');
                        }

                        return (
                            ignore ||
                            triggerCheckIdNo({
                                id_no: value,
                                id_type: idType,
                                customer_id: customerId,
                                process: !!customerId ? 'modify' : 'open'
                            }).catch(error => Promise.reject(error.error_msg))
                        );
                    }

                    if (isSSN && !/^\d{9}$/g.test(value)) {
                        return __('SSN should be a nine-digit number');
                    }

                    return (
                        ignore ||
                        triggerCheckSSN({ id_no: value, source, id_type: idType }).catch(error => {
                            if (error.error_code === CustomerErrorCodeEnum.SSN_ALREADY_ASSOCIATED) {
                                // 针对已关联帐户的特殊提示文案
                                return Promise.reject(
                                    __(
                                        'The entered SSN is already associated with another account. For more information, please contact us via support@tradeup.com.'
                                    )
                                );
                            }

                            return Promise.reject(error.error_msg);
                        })
                    );
                }}
                addons={
                    needHide
                        ? {
                              end: (
                                  <Icon
                                      className={styles.idNoIcon}
                                      fill="gray"
                                      height="20px"
                                      width="20px"
                                      type={passwordShow ? 'eyeInvisible' : 'eye'}
                                      onClick={() => {
                                          setPasswordShow(!passwordShow);
                                      }}
                                  />
                              )
                          }
                        : undefined
                }
                {...restProps}>
                <FormControl
                    placeholder={finalLabel}
                    type={needHide && !passwordShow ? 'password' : 'text'}
                    autoComplete={autoComplete}
                    disabled={disabled}
                />
            </FormGroup>
        </>
    );
};

export default observer(IdNumber);
